"use client";

import { useState, useRef, useEffect } from "react";
import { useLocale } from "next-intl";
import { useRouter, usePathname } from "next/navigation";
import { routing } from "@/i18n/routing";
import { Globe, Check } from "lucide-react";

const localeLabels: Record<string, string> = {
  zh: "中文",
  en: "EN",
  ja: "日本語",
  ko: "한국어",
};

const localeFullLabels: Record<string, string> = {
  zh: "中文",
  en: "English",
  ja: "日本語",
  ko: "한국어",
};

export function LanguageSwitcher() {
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function switchLocale(newLocale: string) {
    const segments = pathname.split("/");
    segments[1] = newLocale;
    router.replace(segments.join("/"));
    setOpen(false);
  }

  return (
    // 🔴 `shrink-0` + 窄屏隐藏文字：390px 下 header 右侧簇是「8 个导航图标 +
    // 语言按钮」，没有收缩保护时会把页面撑出 13px 横向滚动条（实测
    // scrollWidth=403 > innerWidth=390）。文字用 `hidden sm:inline` 让位，
    // Globe 图标本身已足够表意，aria-label 保证读屏仍拿到完整语义。
    <div className="relative shrink-0" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        aria-label={localeFullLabels[locale] ?? localeLabels[locale]}
        aria-expanded={open}
        className="flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-medium text-[var(--text-muted)] transition-colors duration-200 hover:bg-[var(--surface)] hover:text-[var(--text-primary)] cursor-pointer"
      >
        <Globe className="h-3.5 w-3.5" strokeWidth={1.8} />
        <span className="hidden sm:inline">{localeLabels[locale]}</span>
      </button>
      {open && (
        <div className="absolute right-0 top-full z-50 mt-1.5 min-w-[140px] overflow-hidden rounded-xl bg-white p-1 shadow-[0_4px_20px_rgba(0,0,0,0.08)] ring-1 ring-black/[0.04]">
          {routing.locales.map((loc) => (
            <button
              key={loc}
              onClick={() => switchLocale(loc)}
              className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-xs transition-colors duration-150 cursor-pointer ${
                loc === locale
                  ? "bg-[var(--primary)]/[0.06] text-[var(--primary)] font-semibold"
                  : "text-[var(--text-secondary)] hover:bg-[var(--surface)] hover:text-[var(--text-primary)]"
              }`}
            >
              <span>{localeFullLabels[loc]}</span>
              {loc === locale && <Check className="h-3.5 w-3.5" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
